import { profileFieldLimits } from "@beacon/core/access/profile-rules"
import type { OwnProfile } from "@beacon/core/access"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Loader2Icon } from "lucide-react"
import { useId, useState } from "react"
import { toast } from "sonner"
import { refusalOf } from "@/administration/client.ts"
import { describeRefusal } from "@/administration/describe.ts"
import { Button } from "@/primitives/button.tsx"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/primitives/dialog.tsx"
import { Field, FieldLabel } from "@/primitives/field.tsx"
import { Input } from "@/primitives/input.tsx"
import { meQuery, type Me } from "@/sign-in/client.ts"
import { editOf, draftOf, browserTimeZone, seededTimeZone, type ProfileDraft } from "./describe.ts"
import { editProfile, markProfileChecked, ownProfileQuery, photosQuery } from "./client.ts"
import { PhotoChanger } from "./photo-changer.tsx"
import { TimeZoneField } from "./time-zone-field.tsx"

/*
 * The check a Member is asked once, on their first sign-in since Profiles arrived: the name,
 * pronouns, time zone and photo Beacon seeded for them, to confirm or fix. Skipping it, closing it
 * or saving it each mark it checked, so it never shows again and never stands in the way: whatever
 * page they opened is behind it. A blank time zone starts as the browser's.
 */
export function ProfileCheck({ me }: { me: Me }) {
  const profile = useQuery(ownProfileQuery)
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(true)

  /** Closes the check and marks it done, however it ended, so the shell stops showing it. */
  function done() {
    setOpen(false)
    void markProfileChecked()
      .catch(() => undefined)
      .then(() => queryClient.invalidateQueries({ queryKey: meQuery.queryKey, exact: true }))
  }

  if (!profile.data) return null
  return (
    <Dialog open={open} onOpenChange={(next) => !next && done()}>
      {open && <Check me={me} profile={profile.data} onDone={done} />}
    </Dialog>
  )
}

function Check({ me, profile, onDone }: { me: Me; profile: OwnProfile; onDone: () => void }) {
  const id = useId()
  const queryClient = useQueryClient()
  const hasPhoto = useQuery(photosQuery).data?.[me.id] !== undefined
  const [draft, setDraft] = useState<ProfileDraft>(() => ({
    ...draftOf(profile),
    timeZone: seededTimeZone(profile.timeZone, browserTimeZone()) ?? "",
  }))
  const [saving, setSaving] = useState(false)
  const edit = editOf(profile, draft)
  const changes = Object.keys(edit).length > 0
  const set = (field: keyof ProfileDraft) => (value: string) =>
    setDraft((current) => ({ ...current, [field]: value }))

  function save() {
    if (!changes) return onDone()
    setSaving(true)
    void editProfile(edit)
      .then(async () => {
        onDone()
        toast.success("Your Profile is saved.")
        await queryClient.invalidateQueries({ queryKey: ownProfileQuery.queryKey })
      })
      .catch((error: unknown) => toast.error(describeRefusal(refusalOf(error))))
      .finally(() => setSaving(false))
  }

  const text = (field: "firstName" | "lastName" | "pronouns", label: string, hint?: string) => (
    <Field>
      <FieldLabel htmlFor={`${id}-${field}`}>{label}</FieldLabel>
      <Input
        id={`${id}-${field}`}
        value={draft[field]}
        maxLength={profileFieldLimits[field]}
        {...(hint && { placeholder: hint })}
        onChange={(event) => set(field)(event.target.value)}
      />
    </Field>
  )

  return (
    <DialogContent size="md">
      <form
        onSubmit={(event) => {
          event.preventDefault()
          save()
        }}
      >
        <DialogHeader>
          <DialogTitle>Is this you?</DialogTitle>
          <DialogDescription>
            Beacon filled in your Profile from the company's Google Workspace and Slack. Fix
            anything that's wrong, or skip this: you can change it any time on Your Profile.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <PhotoChanger person={{ id: me.id, name: me.name }} hasPhoto={hasPhoto} />
          <div className="grid grid-cols-2 gap-3 compact:grid-cols-1">
            {text("firstName", "First name")}
            {text("lastName", "Last name")}
          </div>
          {text("pronouns", "Pronouns", "she/her, they/them, he/him…")}
          <Field>
            <FieldLabel>Time zone</FieldLabel>
            <TimeZoneField
              value={draft.timeZone || null}
              onChange={set("timeZone")}
              fieldWidth="md"
            />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" disabled={saving} onClick={onDone}>
            Skip
          </Button>
          <Button type="submit" disabled={saving} focusableWhenDisabled={saving}>
            {saving && <Loader2Icon data-icon="inline-start" className="animate-spin" />}
            {changes ? "Save" : "Looks right"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}
